<script>
    import { onMount } from 'svelte';
    import { formatEuroAmount } from './money.js';
    import { getApiBase } from './apiBase.js';

    export let studentId = null;
    export let embedded = false;
    const apiBase = getApiBase();

    // State variables
    let users = [];
    let filteredUsers = [];
    let selectedUserId = null;
    let isLoading = false;
    let isSaving = false;
    let errorMessage = '';
    let successMessage = '';
    let searchQuery = '';
    
    // Filter state
    let activeFilter = 'students'; // 'all', 'students', 'teachers', 'with_modifiers', 'without_modifiers'
    
    // Pricing modifier data
    let pricingModifiers = new Map(); // user_id -> modifier object
    let editingUserId = null;
    let editingModifier = null;
    
    // Manual class discount data
    let selectedMonth = new Date();
    let studentClasses = [];
    let isLoadingClasses = false;
    let currentClassesRequestKey = null; // track latest request to avoid race conditions

    // Per-type base price rules (Subject + Teacher)
    let typeBasePriceRules = [];
    let isLoadingTypeRules = false;
    let selectedTypeKey = '';
    let typeBasePriceInput = '';
    let typeValidFrom = '';
    let typeValidUntil = '';
    let editingTypeRuleId = null;

    function makeTypeKey(subjectName, teacherName) {
        return `${subjectName || ''}|||${teacherName || ''}`;
    }

    // Unique Subject+Teacher combinations for this student across a wider window
    // (recent + upcoming months), loaded independently of the month selector so
    // future class types are selectable. Populated by loadStudentClassTypes().
    let availableTypeOptions = [];
    let isLoadingTypeOptions = false;
    
    
    // Load users on component mount
    onMount(async () => {
        await loadUsers();
        await loadPricingModifiers();
        if (embedded && studentId) {
            const idNum = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
            if (!Number.isNaN(idNum)) {
                await startEditingModifier(idNum);
            }
        }
    });
    
    async function loadUsers() {
        isLoading = true;
        errorMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/users-for-email-selection/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to load users');
            }
            
            const data = await response.json();
            users = data.users || [];
            applyFilters();
            
        } catch (error) {
            errorMessage = `Error loading users: ${error.message}`;
            console.error('Error loading users:', error);
        } finally {
            isLoading = false;
        }
    }
    
    async function loadPricingModifiers() {
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-pricing-modifiers/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                pricingModifiers = new Map();
                data.modifiers.forEach(modifier => {
                    pricingModifiers.set(modifier.student_id, modifier);
                });
                pricingModifiers = pricingModifiers; // Trigger reactivity
            }
            
        } catch (error) {
            console.error('Error loading pricing modifiers:', error);
        }
    }
    
    
    function applyFilters() {
        let filtered = [...users];
        
        // Apply user type filter
        if (activeFilter === 'students') {
            filtered = filtered.filter(user => user.user_type === 'student');
        } else if (activeFilter === 'teachers') {
            filtered = filtered.filter(user => user.user_type === 'teacher');
        } else if (activeFilter === 'with_modifiers') {
            filtered = filtered.filter(user => pricingModifiers.has(user.id));
        } else if (activeFilter === 'without_modifiers') {
            filtered = filtered.filter(user => !pricingModifiers.has(user.id));
        }
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(user => 
                user.first_name.toLowerCase().includes(query) ||
                user.last_name.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query) ||
                user.username.toLowerCase().includes(query)
            );
        }
        
        filteredUsers = filtered;
    }
    
    function setFilter(filter) {
        activeFilter = filter;
        applyFilters();
    }
    
    async function startEditingModifier(userId) {
        editingUserId = userId;
        const existingModifier = pricingModifiers.get(userId);
        selectedTypeKey = '';
        typeBasePriceInput = '';
        typeValidFrom = '';
        typeValidUntil = '';
        editingTypeRuleId = null;
        typeBasePriceRules = [];
        availableTypeOptions = [];
        
        // Default modifier structure (all exemptions false means all modifiers apply by default)
        editingModifier = {
            student_id: userId,
            exempt_from_prime_time_surcharge: false,
            exempt_from_high_activity_discount: false,
            exempt_from_family_discount: false,
            exempt_from_ib_surcharge: false,
            exempt_from_special_needs_surcharge: false,
            exempt_from_lithuanian_surcharge: false,
            custom_base_price: null,
            custom_minimum_price: null,
            custom_group_price_2_students: null,
            custom_group_price_3plus_students: null,
            notes: '',
            is_active: true,
            ...existingModifier
        };
        
        // Load classes for the current month
        await loadStudentClassesForMonth(userId, selectedMonth);

        // Load selectable class types across a wider window (recent + upcoming),
        // independent of the month selector above.
        await loadStudentClassTypes(userId);

        // Load type base price rules for this student
        await loadStudentTypeBasePrices(userId);

        // Default selection for type dropdown
        if (!selectedTypeKey && availableTypeOptions.length > 0) {
            selectedTypeKey = availableTypeOptions[0].key;
        }
    }

    // Keep dropdown selection valid when month/classes change
    $: if (availableTypeOptions.length > 0) {
        const hasSelection = !!selectedTypeKey && availableTypeOptions.some(o => o.key === selectedTypeKey);
        if (!hasSelection) selectedTypeKey = availableTypeOptions[0].key;
    }
    
    // Load classes for selected month
    async function loadStudentClassesForMonth(studentId, month) {
        isLoadingClasses = true;
        const token = localStorage.getItem('jwtToken');
        const monthStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM in local time
        const requestKey = `${studentId}:${monthStr}`;
        currentClassesRequestKey = requestKey;
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-classes-for-discount/?student_id=${studentId}&month=${monthStr}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            // Ignore out-of-order responses
            if (currentClassesRequestKey !== requestKey) {
                return;
            }
            
            if (response.ok) {
                const data = await response.json();
                studentClasses = data.classes || [];
                console.log(`Loaded ${studentClasses.length} classes for month ${monthStr}`);
            } else {
                const errorData = await response.json();
                console.error('Error loading student classes:', errorData.message);
                studentClasses = [];
            }
        } catch (error) {
            // Ignore errors from stale requests
            if (currentClassesRequestKey === requestKey) {
                console.error('Error loading student classes:', error);
                studentClasses = [];
            }
        } finally {
            if (currentClassesRequestKey === requestKey) {
                isLoadingClasses = false;
            }
        }
    }

    async function loadStudentClassTypes(studentId) {
        isLoadingTypeOptions = true;
        const token = localStorage.getItem('jwtToken');
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-class-types/?student_id=${studentId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to load class types');
            }

            availableTypeOptions = (data.types || []).map(t => ({
                key: makeTypeKey(t.subject_name, t.teacher_name),
                subject_name: t.subject_name,
                teacher_name: t.teacher_name,
                label: t.label || `${t.subject_name} — ${t.teacher_name}`
            }));
        } catch (error) {
            console.error('Error loading class types:', error);
            availableTypeOptions = [];
        } finally {
            isLoadingTypeOptions = false;
        }
    }

    async function loadStudentTypeBasePrices(studentId) {
        isLoadingTypeRules = true;
        const token = localStorage.getItem('jwtToken');
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-type-base-prices/?student_id=${studentId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to load type base price rules');
            }

            typeBasePriceRules = data.rules || [];
        } catch (error) {
            console.error('Error loading type base price rules:', error);
            typeBasePriceRules = [];
        } finally {
            isLoadingTypeRules = false;
        }
    }

    function editTypeBasePriceRule(rule) {
        editingTypeRuleId = rule.id;
        selectedTypeKey = makeTypeKey(rule.subject_name, rule.teacher_name);
        typeBasePriceInput = String(rule.base_price);
        typeValidFrom = rule.valid_from || '';
        typeValidUntil = rule.valid_until || '';
    }

    function resetTypeBasePriceForm() {
        editingTypeRuleId = null;
        typeBasePriceInput = '';
        typeValidFrom = '';
        typeValidUntil = '';
    }

    async function upsertTypeBasePriceRule() {
        if (!editingUserId) return;
        if (!selectedTypeKey) {
            errorMessage = 'Please select a class type (Subject — Teacher) first.';
            return;
        }

        const parsed = parseFloat(typeBasePriceInput);
        if (Number.isNaN(parsed) || parsed < 0) {
            errorMessage = 'Base price must be a non-negative number.';
            return;
        }

        const option = availableTypeOptions.find(o => o.key === selectedTypeKey);
        if (!option) {
            errorMessage = 'Selected class type is no longer available. Reload classes and try again.';
            return;
        }

        if (typeValidFrom && typeValidUntil && typeValidFrom > typeValidUntil) {
            errorMessage = 'Valid from date must be on or before the valid until date.';
            return;
        }

        isSaving = true;
        errorMessage = '';
        successMessage = '';
        const token = localStorage.getItem('jwtToken');

        try {
            const endpoint = editingTypeRuleId
                ? `${apiBase}/studentplayground/api/student-type-base-prices/${editingTypeRuleId}/`
                : `${apiBase}/studentplayground/api/student-type-base-prices/`;
            const response = await fetch(endpoint, {
                method: editingTypeRuleId ? 'PATCH' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...(editingTypeRuleId ? {} : { student_id: editingUserId }),
                    subject_name: option.subject_name,
                    teacher_name: option.teacher_name,
                    base_price: parsed,
                    is_active: true,
                    valid_from: typeValidFrom || null,
                    valid_until: typeValidUntil || null
                })
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to save type base price rule');
            }

            // Update local list (replace if exists)
            const saved = data.rule;
            const idx = typeBasePriceRules.findIndex(r => r.id === saved.id);
            if (idx >= 0) {
                typeBasePriceRules[idx] = saved;
                typeBasePriceRules = [...typeBasePriceRules];
            } else {
                typeBasePriceRules = [saved, ...typeBasePriceRules];
            }

            successMessage = editingTypeRuleId ? 'Type price timeframe updated.' : 'Type price timeframe added.';
            resetTypeBasePriceForm();
        } catch (error) {
            errorMessage = `Error saving type base price rule: ${error.message}`;
            console.error('Error saving type base price rule:', error);
        } finally {
            isSaving = false;
        }
    }

    async function toggleTypeBasePriceRuleActive(rule, nextActive) {
        if (!editingUserId || !rule) return;
        isSaving = true;
        errorMessage = '';
        successMessage = '';
        const token = localStorage.getItem('jwtToken');

        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-type-base-prices/${rule.id}/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    is_active: nextActive
                })
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to update rule');
            }

            const saved = data.rule;
            const idx = typeBasePriceRules.findIndex(r => r.id === saved.id);
            if (idx >= 0) {
                typeBasePriceRules[idx] = saved;
                typeBasePriceRules = [...typeBasePriceRules];
            }
        } catch (error) {
            errorMessage = `Error updating rule: ${error.message}`;
        } finally {
            isSaving = false;
        }
    }

    async function deleteTypeBasePriceRule(ruleId) {
        if (!confirm('Delete this type base price rule?')) return;
        isSaving = true;
        errorMessage = '';
        successMessage = '';
        const token = localStorage.getItem('jwtToken');

        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-type-base-prices/${ruleId}/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete rule');
            }
            typeBasePriceRules = typeBasePriceRules.filter(r => r.id !== ruleId);
            if (editingTypeRuleId === ruleId) resetTypeBasePriceForm();
            successMessage = 'Type base price rule deleted.';
        } catch (error) {
            errorMessage = `Error deleting rule: ${error.message}`;
        } finally {
            isSaving = false;
        }
    }
    
    // Manual class price override modal state
    let showOverrideModal = false;
    let selectedClassItem = null;
    let overridePrice = '';
    let overrideNotes = '';

    function isClassOverridden(classItem) {
        return !!(classItem?.is_overridden ?? classItem?.is_discounted);
    }

    function openOverrideModal(classItem) {
        selectedClassItem = classItem;
        // default to current override if present, otherwise show the computed original price
        const overridden = isClassOverridden(classItem);
        const current = overridden ? (classItem.override_amount ?? classItem.price) : (classItem.original_price ?? classItem.price);
        overridePrice = current !== null && current !== undefined ? String(current) : '';
        overrideNotes = classItem.discount_notes || '';
        showOverrideModal = true;
    }

    function closeOverrideModal() {
        showOverrideModal = false;
        selectedClassItem = null;
        overridePrice = '';
        overrideNotes = '';
    }

    async function saveClassOverride() {
        if (!selectedClassItem) return;
        const token = localStorage.getItem('jwtToken');
        const parsed = parseFloat(overridePrice);
        if (Number.isNaN(parsed) || parsed < 0) {
            errorMessage = 'Override price must be a non-negative number.';
            return;
        }

        try {
            const response = await fetch(`${apiBase}/studentplayground/api/manual-class-discount/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    student_id: editingUserId,
                    class_id: selectedClassItem.id,
                    override_amount: parsed,
                    notes: overrideNotes || `Manual override set via admin interface`
                })
            });

            const data = await response.json();

            if (response.ok) {
                await loadStudentClassesForMonth(editingUserId, selectedMonth);
                successMessage = data.message;
                setTimeout(() => { successMessage = ''; }, 3000);
                closeOverrideModal();
            } else {
                errorMessage = `Error saving override: ${data.message}`;
            }
        } catch (error) {
            errorMessage = `Error saving override: ${error.message}`;
        }
    }

    async function clearClassOverride() {
        if (!selectedClassItem) return;
        const token = localStorage.getItem('jwtToken');

        try {
            const response = await fetch(`${apiBase}/studentplayground/api/manual-class-discount/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    student_id: editingUserId,
                    class_id: selectedClassItem.id,
                    clear_override: true,
                    notes: overrideNotes || `Manual override cleared via admin interface`
                })
            });

            const data = await response.json();

            if (response.ok) {
                await loadStudentClassesForMonth(editingUserId, selectedMonth);
                successMessage = data.message;
                setTimeout(() => { successMessage = ''; }, 3000);
                closeOverrideModal();
            } else {
                errorMessage = `Error clearing override: ${data.message}`;
            }
        } catch (error) {
            errorMessage = `Error clearing override: ${error.message}`;
        }
    }

    function setOverrideToZero() {
        overridePrice = '0';
    }
    
    // Month navigation functions
    function previousMonth() {
        selectedMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1);
        if (editingUserId) {
            loadStudentClassesForMonth(editingUserId, selectedMonth);
        }
    }
    
    function nextMonth() {
        selectedMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1);
        if (editingUserId) {
            loadStudentClassesForMonth(editingUserId, selectedMonth);
        }
    }
    
    function formatMonth(date) {
        return date.toLocaleDateString('lt-LT', { year: 'numeric', month: 'long' });
    }
    
    function formatDate(dateString) {
        if (!dateString) return 'No date';
        const date = new Date(dateString);
        return date.toLocaleDateString('lt-LT', { 
            weekday: 'short',
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }
    
    function cancelEditingModifier() {
        editingUserId = null;
        editingModifier = null;
        studentClasses = [];  // Clear classes when closing modal
        typeBasePriceRules = [];
        availableTypeOptions = [];
        selectedTypeKey = '';
        typeBasePriceInput = '';
        typeValidFrom = '';
        typeValidUntil = '';
        editingTypeRuleId = null;
    }
    
    async function saveModifier() {
        if (!editingModifier) return;
        
        isSaving = true;
        errorMessage = '';
        successMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-pricing-modifiers/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editingModifier)
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to save pricing modifier');
            }
            
            // Update local state
            pricingModifiers.set(editingModifier.student_id, data.modifier);
            pricingModifiers = pricingModifiers; // Trigger reactivity
            
            successMessage = `Pricing modifier saved successfully for ${getUserName(editingModifier.student_id)}`;
            cancelEditingModifier();
            applyFilters();
            
        } catch (error) {
            errorMessage = `Error saving modifier: ${error.message}`;
            console.error('Error saving modifier:', error);
        } finally {
            isSaving = false;
        }
    }
    
    async function deleteModifier(userId) {
        if (!confirm(`Are you sure you want to delete the pricing modifier for ${getUserName(userId)}? This will restore default pricing behavior.`)) {
            return;
        }
        
        isSaving = true;
        errorMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-pricing-modifiers/${userId}/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to delete pricing modifier');
            }
            
            // Remove from local state
            pricingModifiers.delete(userId);
            pricingModifiers = pricingModifiers; // Trigger reactivity
            
            successMessage = `Pricing modifier deleted for ${getUserName(userId)}. Default pricing will now apply.`;
            applyFilters();
            
        } catch (error) {
            errorMessage = `Error deleting modifier: ${error.message}`;
            console.error('Error deleting modifier:', error);
        } finally {
            isSaving = false;
        }
    }
    
    function getUserName(userId) {
        const user = users.find(u => u.id === userId);
        return user ? `${user.first_name} ${user.last_name}` : 'Unknown User';
    }
    
    function getModifierSummary(modifier) {
        if (!modifier) return 'Default pricing applies (all discounts/surcharges enabled)';
        
        const exemptions = [];
        if (modifier.exempt_from_prime_time_surcharge) exemptions.push('No Prime Time');
        if (modifier.exempt_from_high_activity_discount) exemptions.push('No Activity Discount');
        if (modifier.exempt_from_family_discount) exemptions.push('No Family Discount');
        if (modifier.exempt_from_ib_surcharge) exemptions.push('No IB Surcharge');
        if (modifier.exempt_from_special_needs_surcharge) exemptions.push('No Special Needs');
        if (modifier.exempt_from_lithuanian_surcharge) exemptions.push('No Lithuanian');
        if (modifier.custom_base_price) exemptions.push(`Custom Base: €${modifier.custom_base_price}`);
        if (modifier.custom_minimum_price) exemptions.push(`Min: €${modifier.custom_minimum_price}`);
        if (modifier.custom_group_price_2_students) exemptions.push(`Custom Group (2): €${modifier.custom_group_price_2_students}`);
        if (modifier.custom_group_price_3plus_students) exemptions.push(`Custom Group (3+): €${modifier.custom_group_price_3plus_students}`);
        
        return exemptions.length > 0 ? exemptions.join(', ') : 'Default pricing applies';
    }
    
    
    // Reactive statements
    $: searchQuery, activeFilter, users, pricingModifiers, applyFilters();
</script>

<div class="pricing-modifier-container {embedded ? 'embedded' : ''}">
    <h3>Student Pricing Modifier Management</h3>
    <p class="description">Manage custom pricing rules for individual students. By default, all pricing modifiers (discounts and surcharges) apply to all students.</p>
    
    <!-- Filter buttons -->
    {#if !embedded}
    <div class="filter-buttons">
        <button 
            class="filter-btn {activeFilter === 'all' ? 'active' : ''}"
            on:click={() => setFilter('all')}
        >
            All Users ({users.length})
        </button>
        <button 
            class="filter-btn {activeFilter === 'students' ? 'active' : ''}"
            on:click={() => setFilter('students')}
        >
            Students ({users.filter(u => u.user_type === 'student').length})
        </button>
        <button 
            class="filter-btn {activeFilter === 'with_modifiers' ? 'active' : ''}"
            on:click={() => setFilter('with_modifiers')}
        >
            With Custom Rules ({pricingModifiers.size})
        </button>
        <button 
            class="filter-btn {activeFilter === 'without_modifiers' ? 'active' : ''}"
            on:click={() => setFilter('without_modifiers')}
        >
            Default Pricing ({users.filter(u => u.user_type === 'student' && !pricingModifiers.has(u.id)).length})
        </button>
    </div>
    
    <!-- Search bar -->
    <div class="search-container">
        <input 
            type="text" 
            placeholder="Search by name, email, or username..." 
            bind:value={searchQuery}
            class="search-input"
        />
    </div>
    
    <!-- Users list -->
    {#if isLoading}
        <div class="loading">Loading users...</div>
    {:else if filteredUsers.length === 0}
        <div class="no-users">No users found</div>
    {:else}
        <div class="users-list">
            {#each filteredUsers as user (user.id)}
                {@const userModifier = pricingModifiers.get(user.id)}
                <div class="user-item {userModifier ? 'has-modifier' : 'default-pricing'}">
                    <div class="user-info">
                        <div class="user-name">
                            {user.first_name} {user.last_name}
                            <span class="user-type {user.user_type}">{user.user_type}</span>
                            {#if userModifier}
                                <span class="modifier-status custom">Custom Rules</span>
                            {:else}
                                <span class="modifier-status default">Default Pricing</span>
                            {/if}
                        </div>
                        <div class="user-email">{user.email}</div>
                        <div class="modifier-summary">
                            {getModifierSummary(userModifier)}
                        </div>
                    </div>
                    <div class="user-actions">
                        <button 
                            class="action-btn edit"
                            on:click={() => startEditingModifier(user.id)}
                            disabled={editingUserId !== null}
                        >
                            {userModifier ? 'Edit Rules' : 'Add Custom Rules'}
                        </button>
                        {#if userModifier}
                            <button 
                                class="action-btn delete"
                                on:click={() => deleteModifier(user.id)}
                                disabled={isSaving}
                            >
                                Reset to Default
                            </button>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    {/if}
    {/if}
    
    <!-- Editing modal -->
    {#if editingUserId && editingModifier}
        <div
            class="modal-overlay {embedded ? 'embedded' : ''}"
            on:click={() => !embedded && cancelEditingModifier()}
            on:keydown={(e) => !embedded && e.key === 'Escape' && cancelEditingModifier()}
        >
            <div class="modal-content {embedded ? 'embedded' : ''}" on:click|stopPropagation on:keydown|stopPropagation>
                <div class="modal-header">
                    <h4>Pricing Rules for {getUserName(editingUserId)}</h4>
                    {#if !embedded}
                        <button class="close-btn" on:click={cancelEditingModifier}>×</button>
                    {/if}
                </div>
                
                <div class="modal-body">
                    <div class="section">
                        <h5>Legacy-only surcharge exemptions</h5>
                        <p class="section-description">These retained fields affect only classes through 2026-08-31. They do not change the simplified pricing that starts on 2026-09-01.</p>
                        
                        <label class="checkbox-label">
                            <input 
                                type="checkbox" 
                                bind:checked={editingModifier.exempt_from_prime_time_surcharge}
                            />
                            <span>Legacy only: exempt from Prime Time Surcharge (+€3 Mon-Thu 5pm-8pm)</span>
                        </label>
                        
                        <label class="checkbox-label">
                            <input 
                                type="checkbox" 
                                bind:checked={editingModifier.exempt_from_ib_surcharge}
                            />
                            <span>Legacy only: exempt from IB Surcharge (+€5)</span>
                        </label>
                        
                        <label class="checkbox-label">
                            <input 
                                type="checkbox" 
                                bind:checked={editingModifier.exempt_from_special_needs_surcharge}
                            />
                            <span>Legacy only: exempt from Special Needs Surcharge (+€3)</span>
                        </label>
                        
                        <label class="checkbox-label">
                            <input 
                                type="checkbox" 
                                bind:checked={editingModifier.exempt_from_lithuanian_surcharge}
                            />
                            <span>Legacy only: exempt from Lithuanian Second Language Surcharge (+€3)</span>
                        </label>
                    </div>
                    
                    <div class="section">
                        <h5>Discount exemptions</h5>
                        <p class="section-description">For classes from 2026-09-01, check this box to prevent the €2 weekly frequency discount. Active, non-cancelled lessons across every teacher, subject, and class type are counted in the class’s Lithuanian Monday–Sunday week.</p>
                        
                        <label class="checkbox-label">
                            <input 
                                type="checkbox" 
                                bind:checked={editingModifier.exempt_from_high_activity_discount}
                            />
                            <span>Exempt from Weekly Frequency Discount (-€2)</span>
                        </label>
                        
                        <label class="checkbox-label">
                            <input 
                                type="checkbox" 
                                bind:checked={editingModifier.exempt_from_family_discount}
                            />
                            <span>Exempt this student from Family Discount (-€2 when 2+ family members have non-cancelled lessons in the same month)</span>
                        </label>
                    </div>
                    
                    <div class="section">
                        <h5>Custom Pricing</h5>
                        <p class="section-description">From 2026-09-01, standard prices are €27 (grades 1–9), €29 (grades 10–12, university, missing or invalid grade), and €16 per student in every class of two or more students. A custom base can still receive the €2 weekly frequency discount; custom group and final manual/timeframe prices are final.</p>
                        
                        <div class="form-group">
                            <label for="custom-base-price">Custom Base Price (€):</label>
                            <input 
                                id="custom-base-price"
                                type="number" 
                                step="0.01"
                                min="0"
                                bind:value={editingModifier.custom_base_price}
                                placeholder="Leave empty for standard €27 / €29 pricing"
                                class="form-input"
                            />
                        </div>
                        
                        <div class="form-group">
                            <label for="custom-minimum-price">Custom Minimum Price (€):</label>
                            <input 
                                id="custom-minimum-price"
                                type="number" 
                                step="0.01"
                                min="0"
                                bind:value={editingModifier.custom_minimum_price}
                                placeholder="Default minimum: €5"
                                class="form-input"
                            />
                        </div>
                        
                        <div class="form-group">
                            <label for="custom-group-price-2">Custom Group Price (2 students) (€):</label>
                            <input 
                                id="custom-group-price-2"
                                type="number" 
                                step="0.01"
                                min="0"
                                bind:value={editingModifier.custom_group_price_2_students}
                                placeholder="Leave empty for standard €16"
                                class="form-input"
                            />
                        </div>
                        
                        <div class="form-group">
                            <label for="custom-group-price-3plus">Custom Group Price (3+ students) (€):</label>
                            <input 
                                id="custom-group-price-3plus"
                                type="number" 
                                step="0.01"
                                min="0"
                                bind:value={editingModifier.custom_group_price_3plus_students}
                                placeholder="Leave empty for standard €16"
                                class="form-input"
                            />
                        </div>

                        <div class="type-pricing-subsection">
                            <h6>Per-Type Base Price (Subject + Teacher)</h6>
                            <p class="section-description">Set final prices for date ranges of a subject and teacher. Date ranges are inclusive, apply to individual and family classes, and may not overlap even when inactive. A matching timeframe price is final and does not receive the weekly discount.</p>

                            {#if isLoadingTypeOptions}
                                <p class="loading-classes">Loading class types...</p>
                            {:else if availableTypeOptions.length === 0}
                                <p class="no-types">No recent or upcoming class types found for this student.</p>
                            {:else}
                                <div class="type-pricing-form">
                                    <div class="form-group">
                                        <label for="type-key">Class Type:</label>
                                        <select id="type-key" class="form-input" bind:value={selectedTypeKey} disabled={editingTypeRuleId !== null}>
                                            {#each availableTypeOptions as opt (opt.key)}
                                                <option value={opt.key}>{opt.label}</option>
                                            {/each}
                                        </select>
                                    </div>

                                    <div class="form-group">
                                        <label for="type-base-price">Type Base Price (€):</label>
                                        <input
                                            id="type-base-price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            bind:value={typeBasePriceInput}
                                            placeholder="e.g. 22"
                                            class="form-input"
                                        />
                                    </div>

                                    <div class="form-group">
                                        <label for="type-valid-from">Valid From (optional):</label>
                                        <input
                                            id="type-valid-from"
                                            type="date"
                                            bind:value={typeValidFrom}
                                            class="form-input"
                                        />
                                    </div>

                                    <div class="form-group">
                                        <label for="type-valid-until">Valid Until (optional):</label>
                                        <input
                                            id="type-valid-until"
                                            type="date"
                                            bind:value={typeValidUntil}
                                            class="form-input"
                                        />
                                    </div>

                                    <div class="form-actions">
                                        <button type="button" class="save-btn" on:click={upsertTypeBasePriceRule} disabled={isSaving}>
                                            {editingTypeRuleId ? 'Update Timeframe' : 'Add Timeframe'}
                                        </button>
                                        {#if editingTypeRuleId}
                                            <button type="button" class="cancel-btn" on:click={resetTypeBasePriceForm} disabled={isSaving}>Cancel Edit</button>
                                        {/if}
                                    </div>
                                </div>
                            {/if}

                            {#if isLoadingTypeRules}
                                <p class="loading-classes">Loading type pricing rules...</p>
                            {:else if typeBasePriceRules.length === 0}
                                <p class="no-types">No type pricing rules yet.</p>
                            {:else}
                                <div class="type-rules-table">
                                    <div class="type-rules-header">
                                        <span>Subject</span>
                                        <span>Teacher</span>
                                        <span>Price</span>
                                        <span>From</span>
                                        <span>Until</span>
                                        <span>Active</span>
                                        <span></span>
                                    </div>
                                    {#each typeBasePriceRules as rule (rule.id)}
                                        <div class="type-rules-row">
                                            <span class="cell">{rule.subject_name}</span>
                                            <span class="cell">{rule.teacher_name}</span>
                                            <span class="cell">€{formatEuroAmount(rule.base_price)}</span>
                                            <span class="cell">{rule.valid_from || '—'}</span>
                                            <span class="cell">{rule.valid_until || '—'}</span>
                                            <span class="cell">
                                                <input
                                                    type="checkbox"
                                                    checked={!!rule.is_active}
                                                    on:change={(e) => toggleTypeBasePriceRuleActive(rule, e.currentTarget.checked)}
                                                />
                                            </span>
                                            <span class="cell actions">
                                                <button type="button" class="edit-btn" on:click={() => editTypeBasePriceRule(rule)} disabled={isSaving}>
                                                    Edit
                                                </button>
                                                <button type="button" class="delete-btn" on:click={() => deleteTypeBasePriceRule(rule.id)} disabled={isSaving}>
                                                    Delete
                                                </button>
                                            </span>
                                        </div>
                                    {/each}
                                </div>
                            {/if}
                        </div>
                    </div>
                    
                    <div class="section">
                        <h5>Manual Class Price Overrides</h5>
                        <p class="section-description">Set a final per-student price for individual classes (ignores the pricing engine). Use month navigation to browse different months:</p>
                        
                        <!-- Month selector -->
                        <div class="month-selector">
                            <button type="button" class="month-nav-btn" on:click={previousMonth}>&lt;</button>
                            <span class="month-display">{formatMonth(selectedMonth)}</span>
                            <button type="button" class="month-nav-btn" on:click={nextMonth}>&gt;</button>
                        </div>
                        
                        <!-- Class list -->
                        {#if isLoadingClasses}
                            <p class="loading-classes">Loading classes...</p>
                        {:else if studentClasses.length === 0}
                            <p class="no-classes">No classes found for this month.</p>
                        {:else}
                            <div class="classes-grid">
                                {#each studentClasses as classItem (classItem.id)}
                                    {@const isFutureClass = new Date(classItem.date_local) > new Date()}
                                    {@const overridden = isClassOverridden(classItem)}
                                    <label
                                        class="class-discount-label"
                                        class:discounted={overridden}
                                        class:no-transaction={!classItem.transaction_found && !isFutureClass}
                                        class:future-class={isFutureClass}
                                        role="button"
                                        tabindex="0"
                                        on:click={() => openOverrideModal(classItem)}
                                        on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && openOverrideModal(classItem)}
                                    >
                                        <div class="class-info">
                                            <span class="class-name">{classItem.name}</span>
                                            <span class="class-date">{formatDate(classItem.date_local)}</span>
                                            {#if overridden}
                                                <span class="class-price" class:discounted-price={overridden}>
                                                    {#if classItem.original_price > 0}
                                                        €{formatEuroAmount(classItem.override_amount ?? classItem.price)}
                                                        <span class="original-price">(was €{formatEuroAmount(classItem.original_price)})</span>
                                                    {:else}
                                                        €{formatEuroAmount(classItem.override_amount ?? classItem.price)}
                                                    {/if}
                                                </span>
                                            {/if}
                                            {#if !classItem.transaction_found && !isFutureClass}
                                                <span class="warning">⚠️ No transaction found</span>
                                            {:else if classItem.pricing_error}
                                                <span class="warning">⚠️ Price calculation failed; no fallback price was applied</span>
                                            {:else if isFutureClass && !overridden}
                                                <span class="info-badge">📅 Pre-emptive override available</span>
                                            {:else if isFutureClass && overridden}
                                                <span class="info-badge">✅ Pre-emptive override applied</span>
                                            {/if}
                                        </div>
                                    </label>
                                {/each}
                            </div>
                            
                            <div class="discount-summary">
                                {#if studentClasses.length > 0}
                                    {@const totalClasses = studentClasses.length}
                                    {@const overriddenClasses = studentClasses.filter(c => isClassOverridden(c)).length}
                                    {@const originalTotal = studentClasses.reduce((sum, c) => sum + (c.original_price || 0), 0)}
                                    {@const currentTotal = studentClasses.reduce((sum, c) => sum + (c.price || 0), 0)}
                                    
                                    <p><strong>Summary:</strong> {overriddenClasses} of {totalClasses} classes overridden</p>
                                    <p><strong>Total:</strong> €{currentTotal.toFixed(2)} 
                                        {#if originalTotal > currentTotal}
                                            <span class="original-price">(was €{originalTotal.toFixed(2)})</span>
                                        {/if}
                                    </p>
                                {/if}
                            </div>
                        {/if}
                    </div>

                    {#if showOverrideModal && selectedClassItem}
                        <div class="modal-overlay" on:click={closeOverrideModal} on:keydown={(e) => e.key === 'Escape' && closeOverrideModal()}>
                            <div class="modal-content" on:click|stopPropagation on:keydown|stopPropagation>
                                <div class="modal-header">
                                    <h4>Manual Price Override</h4>
                                    <button class="close-btn" on:click={closeOverrideModal}>×</button>
                                </div>
                                <div class="modal-body">
                                    <p><strong>Class:</strong> {selectedClassItem.name}</p>
                                    <p><strong>Date:</strong> {formatDate(selectedClassItem.date_local)}</p>

                                    <div class="form-group">
                                        <label for="override-price">New final price (€):</label>
                                        <input
                                            id="override-price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            bind:value={overridePrice}
                                            class="form-input"
                                        />
                                    </div>

                                    <div class="form-group">
                                        <label for="override-notes">Notes (optional):</label>
                                        <textarea
                                            id="override-notes"
                                            rows="3"
                                            bind:value={overrideNotes}
                                            class="form-input"
                                            placeholder="Why was this override applied?"
                                        />
                                    </div>
                                </div>
                                <div class="modal-footer">
                                    <button type="button" class="action-btn delete" on:click={clearClassOverride}>
                                        Clear override
                                    </button>
                                    <button type="button" class="action-btn" on:click={setOverrideToZero}>
                                        Set to €0
                                    </button>
                                    <button type="button" class="action-btn edit" on:click={saveClassOverride}>
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    {/if}
                    
                    <div class="section">
                        <h5>Notes</h5>
                        <textarea 
                            bind:value={editingModifier.notes}
                            placeholder="Internal notes about why this pricing modifier was applied..."
                            rows="3"
                            class="form-textarea"
                        ></textarea>
                    </div>
                    
                    <div class="section">
                        <label class="checkbox-label">
                            <input 
                                type="checkbox" 
                                bind:checked={editingModifier.is_active}
                            />
                            <span><strong>Active</strong> (uncheck to temporarily disable this modifier)</span>
                        </label>
                    </div>
                </div>
                
                <div class="modal-footer">
                    {#if !embedded}
                        <button 
                            class="btn secondary"
                            on:click={cancelEditingModifier}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                    {/if}
                    <button 
                        class="btn primary"
                        on:click={saveModifier}
                        disabled={isSaving}
                    >
                        {#if isSaving}
                            Saving...
                        {:else}
                            Save Pricing Rules
                        {/if}
                    </button>
                </div>
            </div>
        </div>
    {/if}
    
    <!-- Messages -->
    {#if errorMessage}
        <div class="error-message">{errorMessage}</div>
    {/if}
    
    {#if successMessage}
        <div class="success-message">{successMessage}</div>
    {/if}
</div>

<style>
    .pricing-modifier-container {
        max-width: 900px;
        margin: 0 auto;
        padding: 20px;
    }

    .pricing-modifier-container.embedded {
        max-width: none;
        margin: 0;
        padding: 0;
    }
    
    .description {
        color: #666;
        margin-bottom: 20px;
        font-style: italic;
    }
    
    .filter-buttons {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }
    
    .filter-btn {
        padding: 8px 16px;
        border: 1px solid #ddd;
        background: white;
        cursor: pointer;
        border-radius: 6px;
        font-size: 14px;
        transition: all 0.2s ease;
    }
    
    .filter-btn.active {
        background: #fab45a;
        color: black;
        border-color: #fab45a;
    }
    
    .filter-btn:hover:not(.active) {
        background: #f5f5f5;
    }
    
    .search-container {
        margin-bottom: 20px;
    }
    
    .search-input {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
    }
    
    .loading, .no-users {
        text-align: center;
        padding: 40px;
        color: #666;
        font-style: italic;
    }
    
    .users-list {
        background: white;
        border: 1px solid #ddd;
        border-radius: 8px;
        overflow: hidden;
    }
    
    .user-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
        transition: background-color 0.2s ease;
    }
    
    .user-item:last-child {
        border-bottom: none;
    }
    
    .user-item:hover {
        background-color: #f9f9f9;
    }
    
    .user-item.has-modifier {
        background-color: #fff8e1;
    }
    
    .user-item.has-modifier:hover {
        background-color: #fff3c4;
    }
    
    .user-info {
        flex: 1;
    }
    
    .user-name {
        font-weight: 500;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }
    
    .user-type {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
    }
    
    .user-type.student {
        background-color: #e3f2fd;
        color: #1976d2;
    }
    
    .user-type.teacher {
        background-color: #f3e5f5;
        color: #7b1fa2;
    }
    
    .modifier-status {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
    }
    
    .modifier-status.custom {
        background-color: #fff3cd;
        color: #856404;
    }
    
    .modifier-status.default {
        background-color: #d4edda;
        color: #155724;
    }
    
    .user-email {
        color: #666;
        font-size: 13px;
        margin-bottom: 4px;
    }
    
    .modifier-summary {
        color: #666;
        font-size: 12px;
        font-style: italic;
    }
    
    .user-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
    }
    
    .action-btn {
        padding: 6px 12px;
        border: 1px solid;
        background: white;
        cursor: pointer;
        border-radius: 4px;
        font-size: 12px;
        transition: all 0.2s ease;
    }
    
    .action-btn.edit {
        border-color: #007bff;
        color: #007bff;
    }
    
    .action-btn.edit:hover:not(:disabled) {
        background: #007bff;
        color: white;
    }
    
    .action-btn.delete {
        border-color: #dc3545;
        color: #dc3545;
    }
    
    .action-btn.delete:hover:not(:disabled) {
        background: #dc3545;
        color: white;
    }
    
    .action-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    
    /* Modal styles */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 20px;
    }

    .modal-overlay.embedded {
        position: static;
        top: auto;
        left: auto;
        right: auto;
        bottom: auto;
        background: transparent;
        z-index: auto;
        padding: 0;
        display: block;
    }
    
    .modal-content {
        background: white;
        border-radius: 8px;
        max-width: 600px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .modal-content.embedded {
        max-width: none;
        max-height: none;
        height: auto;
        overflow: visible;
        box-shadow: none;
        border: 1px solid rgba(0, 0, 0, 0.08);
    }
    
    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #eee;
        background: #f8f9fa;
        border-radius: 8px 8px 0 0;
    }
    
    .modal-header h4 {
        margin: 0;
        color: #333;
    }
    
    .close-btn {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
    }
    
    .close-btn:hover {
        background: #e9ecef;
        color: #333;
    }
    
    .modal-body {
        padding: 20px;
    }
    
    .section {
        margin-bottom: 25px;
        padding-bottom: 20px;
        border-bottom: 1px solid #f0f0f0;
    }
    
    .section:last-child {
        border-bottom: none;
        margin-bottom: 0;
    }
    
    .section h5 {
        margin: 0 0 8px 0;
        color: #333;
        font-size: 16px;
    }
    
    .section-description {
        margin: 0 0 15px 0;
        color: #666;
        font-size: 13px;
        font-style: italic;
    }
    
    .checkbox-label {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin-bottom: 12px;
        cursor: pointer;
        padding: 8px;
        border-radius: 4px;
        transition: background-color 0.2s ease;
    }
    
    .checkbox-label:hover {
        background-color: #f8f9fa;
    }
    
    .checkbox-label input[type="checkbox"] {
        margin: 2px 0 0 0;
        cursor: pointer;
    }
    
    .checkbox-label span {
        font-size: 14px;
        line-height: 1.4;
    }
    
    .form-group {
        margin-bottom: 15px;
    }
    
    .form-group label {
        display: block;
        margin-bottom: 5px;
        font-weight: 500;
        color: #333;
        font-size: 14px;
    }
    
    .form-input, .form-textarea {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        font-family: inherit;
    }

    .type-pricing-subsection {
        margin-top: 18px;
        padding-top: 12px;
        border-top: 1px solid #eee;
    }

    .type-pricing-subsection h6 {
        margin: 0 0 6px 0;
        font-size: 14px;
        font-weight: 700;
        color: #333;
    }

    .type-pricing-form {
        display: grid;
        grid-template-columns: 1fr 220px;
        gap: 12px;
        align-items: end;
        margin-bottom: 12px;
    }

    .type-pricing-form .form-actions {
        grid-column: 1 / -1;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .type-rules-table {
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        overflow: hidden;
        background: #fff;
    }

    .type-rules-header,
    .type-rules-row {
        display: grid;
        grid-template-columns: 1.2fr 1.4fr 90px 110px 110px 70px 140px;
        gap: 10px;
        align-items: center;
        padding: 8px 10px;
    }

    .type-rules-header {
        background: #f8f9fa;
        font-weight: 700;
        font-size: 12px;
        border-bottom: 1px solid #e0e0e0;
        color: #333;
    }

    .type-rules-row {
        font-size: 13px;
        border-bottom: 1px solid #f0f0f0;
    }

    .type-rules-row:last-child {
        border-bottom: none;
    }

    .type-rules-row .actions {
        display: flex;
        justify-content: flex-end;
        gap: 6px;
    }

    .edit-btn {
        border: 1px solid #14695b;
        border-radius: 4px;
        padding: 5px 8px;
        background: #fff;
        color: #14695b;
        cursor: pointer;
    }

    .no-types {
        color: #666;
        font-style: italic;
        margin: 8px 0;
    }
    
    .form-textarea {
        resize: vertical;
        min-height: 80px;
    }
    
    /* Manual Class Discount Styles */
    .month-selector {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 15px;
        margin-bottom: 20px;
        padding: 10px;
        background: #f8f9fa;
        border-radius: 6px;
    }
    
    .month-nav-btn {
        background: #007bff;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 8px 12px;
        cursor: pointer;
        font-weight: bold;
        transition: background-color 0.2s ease;
    }
    
    .month-nav-btn:hover {
        background: #0056b3;
    }
    
    .month-display {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        min-width: 150px;
        text-align: center;
    }
    
    .loading-classes, .no-classes {
        text-align: center;
        padding: 20px;
        color: #666;
        font-style: italic;
        background: #f8f9fa;
        border-radius: 6px;
        border: 1px solid #e0e0e0;
    }
    
    .classes-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 8px;
        margin-bottom: 20px;
        max-height: 300px;
        overflow-y: auto;
        padding: 10px;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        background: #fafafa;
    }
    
    .class-discount-label {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .class-discount-label:hover {
        background: #f0f8ff;
        border-color: #007bff;
    }
    
    .class-discount-label.discounted {
        background: #d4edda;
        border-color: #28a745;
    }
    
    .class-discount-label.discounted:hover {
        background: #c3e6cb;
    }
    
    .class-discount-label.no-transaction {
        background: #f8d7da;
        border-color: #dc3545;
        cursor: not-allowed;
    }
    
    .class-discount-label.no-transaction:hover {
        background: #f5c6cb;
    }
    
    .class-discount-label.future-class {
        background: #e7f3ff;
        border-color: #2196f3;
    }
    
    .class-discount-label.future-class:hover {
        background: #d0e7ff;
    }
    
    .class-discount-label.future-class.discounted {
        background: #e8f5e9;
        border-color: #4caf50;
    }
    
    .class-discount-label.future-class.discounted:hover {
        background: #c8e6c9;
    }
    
    .class-discount-label input[type="checkbox"] {
        margin-top: 2px;
        cursor: pointer;
    }
    
    .class-discount-label input[type="checkbox"]:disabled {
        cursor: not-allowed;
    }
    
    .class-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    
    .class-name {
        font-weight: 600;
        color: #333;
        font-size: 14px;
        line-height: 1.3;
    }
    
    .class-date {
        font-size: 12px;
        color: #666;
    }
    
    .class-price {
        font-size: 13px;
        font-weight: 600;
        color: #28a745;
    }
    
    .class-price.discounted-price {
        color: #007bff;
    }
    
    .original-price {
        font-size: 11px;
        color: #999;
        text-decoration: line-through;
        font-weight: normal;
    }
    
    .warning {
        font-size: 11px;
        color: #dc3545;
        font-weight: 600;
    }
    
    .info-badge {
        font-size: 11px;
        color: #2196f3;
        font-weight: 600;
    }
    
    .future-badge {
        font-size: 11px;
        color: #2196f3;
        font-weight: 600;
        background: #e3f2fd;
        padding: 2px 6px;
        border-radius: 3px;
        margin-left: 5px;
    }
    
    .detection-method {
        font-size: 10px;
        color: #6c757d;
        font-style: italic;
    }
    
    .discount-summary {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 6px;
        border: 1px solid #e9ecef;
        margin-top: 15px;
    }
    
    .discount-summary p {
        margin: 5px 0;
        font-size: 14px;
        line-height: 1.4;
        color: #495057;
    }
    
    
    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 20px;
        border-top: 1px solid #eee;
        background: #f8f9fa;
        border-radius: 0 0 8px 8px;
    }
    
    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .btn.primary {
        background: #28a745;
        color: white;
    }
    
    .btn.primary:hover:not(:disabled) {
        background: #218838;
    }
    
    .btn.secondary {
        background: #6c757d;
        color: white;
    }
    
    .btn.secondary:hover:not(:disabled) {
        background: #545b62;
    }
    
    .btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    
    .error-message {
        background: #f8d7da;
        color: #721c24;
        padding: 12px;
        border-radius: 6px;
        margin-top: 20px;
        border: 1px solid #f5c6cb;
    }
    
    .success-message {
        background: #d4edda;
        color: #155724;
        padding: 12px;
        border-radius: 6px;
        margin-top: 20px;
        border: 1px solid #c3e6cb;
    }
    
    /* Responsive design */
    @media (max-width: 768px) {
        .pricing-modifier-container {
            padding: 15px;
        }
        
        .filter-buttons {
            flex-direction: column;
        }
        
        .user-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
        }
        
        .user-actions {
            align-self: stretch;
            justify-content: space-between;
        }
        
        .modal-overlay {
            padding: 10px;
        }
        
        .modal-content {
            max-height: 95vh;
        }
        
        .modal-footer {
            flex-direction: column;
        }
    }
</style>
